import dayjs from 'dayjs';
import { dashboardAPI as API } from '../../../axiosConfig';

const {
  createSlice,
  createAsyncThunk,
  createSelector,
} = require('@reduxjs/toolkit');

const URL = 'proxy?endpoint=supernova/rule';

const SLICE_NAME = 'paymentOrchestrationRules';
const GET_PAYMENT_ORCHESTRATION_RULE_LIST = `${SLICE_NAME}/getRuleList`;
const GET_PAYMENT_ORCHESTRATION_RULE = `${SLICE_NAME}/rules/{id}`;
const ADD_PAYMENT_ORCHESTRATION_RULE = `${SLICE_NAME}/rules/add`;
const UPDATE_PAYMENT_ORCHESTRATION_RULE = `${SLICE_NAME}/rules/{id}/update`;
const DELETE_PAYMENT_ORCHESTRATION_RULE = `${SLICE_NAME}/rules/{id}/delete`;
const FORCE_DISABLE_PAYMENT_ORCHESTRATION_RULE = `${SLICE_NAME}/rules/{id}/forceDisable`;
const GET_RULE_COLLECTED_COMMISSION = `${SLICE_NAME}/rule/collected-commission`;

export const addRule = createAsyncThunk(
  ADD_PAYMENT_ORCHESTRATION_RULE,
  async (payload) => {
    const response = await API.post(URL, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });

    return response.data;
  },
);

export const updateRule = createAsyncThunk(
  UPDATE_PAYMENT_ORCHESTRATION_RULE,
  async (payload) => {
    const response = await API.put(`${URL}/${payload.id}`, payload).catch(
      (error) => {
        console.error(JSON.stringify(error));
        return error && error.response;
      },
    );

    return response.data;
  },
);

export const deleteRule = createAsyncThunk(
  DELETE_PAYMENT_ORCHESTRATION_RULE,
  async ({ id, user, headers }, { rejectWithValue }) => {
    try {
      const { data } = await API.delete(`${URL}/${id}?user=${user}`, {
        headers,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const forceDisableRule = createAsyncThunk(
  FORCE_DISABLE_PAYMENT_ORCHESTRATION_RULE,
  async ({ id, user, headers }, { rejectWithValue }) => {
    try {
      const { data } = await API.put(
        `${URL}/${id}/force-disable?user=${user}`,
        {
          headers,
        },
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getRuleDetails = createAsyncThunk(
  GET_PAYMENT_ORCHESTRATION_RULE,
  async (id) => {
    const response = await API.get(`${URL}/${id}`).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });

    return response.data;
  },
);

export const getRuleCommissionCollected = createAsyncThunk(
  GET_RULE_COLLECTED_COMMISSION,
  async (id) => {
    const url = `${URL}/${id}/collectedcommission`;

    const response = await API.get(url).catch((error) => {
      console.error(JSON.stringify(error));
      s;
      return error && error.response;
    });

    return response.data;
  },
);

export const getPaymentOrchestrationRuleList = createAsyncThunk(
  GET_PAYMENT_ORCHESTRATION_RULE_LIST,
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await API.get(URL);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState = {
  ruleList: [],
  ruleListStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',
  selectedRule: null,
  selectedRuleLimits: null,
  ruleCollectedCommissionLoading: false,
  ruleCollectedCommission: '',

  filteredSource: [],
  filteredStatus: ['Enabled'],
  filteredEffectiveStartDate: null,
  filteredEffectiveEndDate: null,
};

const paymentOrchestrationRulesSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetSelectedRule(state) {
      state.selectedRule = null;
    },
    resetCollectedCommission(state) {
      state.ruleCollectedCommission = '';
    },
    resetFilters(state) {
      state.filteredSource = [];
      state.filteredStatus = [];
      state.filteredEffectiveStartDate = null;
      state.filteredEffectiveEndDate = null;
    },
    setFilterSources(state, { payload }) {
      state.filteredSource = payload;
    },
    setFilterStatuses(state, { payload }) {
      state.filteredStatus = payload;
    },
    setFilterEffectiveStartDate(state, { payload }) {
      state.filteredEffectiveStartDate = payload;
    },
    setFilterEffectiveEndDate(state, { payload }) {
      state.filteredEffectiveEndDate = payload;
    },
    removeFilterSource(state, { payload }) {
      const existingSources = state.filteredSource;
      const newFilteredSources = existingSources.filter(
        (source) => source !== payload,
      );
      state.filteredSource = newFilteredSources;
    },
    removeFilterStatus(state, { payload }) {
      const existingStatus = state.filteredStatus;
      const newFilteredStatus = existingStatus.filter(
        (status) => status !== payload,
      );
      state.filteredStatus = newFilteredStatus;
    },
    removeFilterEffectiveDateRange(state) {
      state.filteredEffectiveStartDate = null;
      state.filteredEffectiveEndDate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentOrchestrationRuleList.pending, (state) => {
        state.ruleListStatus = 'loading';
      })
      .addCase(getPaymentOrchestrationRuleList.rejected, (state) => {
        state.ruleList = [];
        state.ruleListStatus = 'failed';
      })
      .addCase(
        getPaymentOrchestrationRuleList.fulfilled,
        (state, { payload }) => {
          state.ruleList = payload;
          state.ruleListStatus = 'succeeded';
        },
      )
      .addCase(getRuleDetails.rejected, (state) => {
        state.selectedRule = null;
        state.selectedRuleLimits = null;
      })
      .addCase(getRuleDetails.fulfilled, (state, { payload }) => {
        state.selectedRule = payload.data ? payload.data[0] : null;
        state.selectedRuleLimits = payload.data ? payload.limits : null;
      })
      .addCase(getRuleCommissionCollected.pending, (state) => {
        state.ruleCollectedCommissionLoading = true;
      })
      .addCase(getRuleCommissionCollected.fulfilled, (state, { payload }) => {
        state.ruleCollectedCommissionLoading = false;
        state.ruleCollectedCommission = payload;
      })
      .addCase(getRuleCommissionCollected.rejected, (state, { payload }) => {
        state.ruleCollectedCommissionLoading = false;
      });
  },
});

export const paymentOrchestrationRulesActions =
  paymentOrchestrationRulesSlice.actions;

export const selectPaymentOrchestrationRuleList = (state) =>
  state.paymentOrchestration.rules.ruleList;

export const selectIsLoadingPaymentOrchestrationRuleList = (state) =>
  state.paymentOrchestration.rules.ruleListStatus === 'loading';

export const selectSelectedOrchestrationRule = (state) =>
  state.paymentOrchestration.rules.selectedRule;

export const selectSelectedOrchestrationRuleLimits = (state) =>
  state.paymentOrchestration.rules.selectedRuleLimits;

export const selectRuleCollectedCommission = (state) =>
  state.paymentOrchestration.rules.ruleCollectedCommission;

export const selectRuleCollectedCommissionLoading = (state) =>
  state.paymentOrchestration.rules.ruleCollectedCommissionLoading;

export const selectFilteredSource = (state) =>
  state.paymentOrchestration.rules.filteredSource;

export const selectFilteredStatus = (state) =>
  state.paymentOrchestration.rules.filteredStatus;

export const selectFilteredEffectiveStartDate = (state) =>
  state.paymentOrchestration.rules.filteredEffectiveStartDate;

export const selectFilteredEffectiveEndDate = (state) =>
  state.paymentOrchestration.rules.filteredEffectiveEndDate;

export const selectAvailableRuleSources = (state) => [
  ...new Set(
    state.paymentOrchestration.rules.ruleList.map(({ source }) => source),
  ),
];

export const selectAvailableRuleStatus = (state) => [
  ...new Set(
    state.paymentOrchestration.rules.ruleList.map(({ status }) => status),
  ),
];

export const selectOrchestrationRuleFilteredList = createSelector(
  selectPaymentOrchestrationRuleList,
  selectFilteredSource,
  selectFilteredStatus,
  selectFilteredEffectiveStartDate,
  selectFilteredEffectiveEndDate,
  (
    rules,
    filteredSource,
    filteredStatus,
    filteredEffectiveStartDate,
    filteredEffectiveEndDate,
  ) => {
    let filteredRules = rules;

    if (filteredSource.length) {
      filteredRules = filteredRules.filter(({ source }) =>
        filteredSource.includes(source),
      );
    }

    if (filteredStatus.length) {
      filteredRules = filteredRules.filter(({ status }) =>
        filteredStatus.includes(status),
      );
    }

    if (filteredEffectiveStartDate || filteredEffectiveEndDate) {
      filteredRules = filteredRules.filter(
        ({ effectiveStartDate, effectiveEndDate }) => {
          const isEffectiveStartDateInFilterRange = dayjs(
            effectiveStartDate,
          ).isBetween(filteredEffectiveStartDate, filteredEffectiveEndDate);
          const isEffectiveEndDateInFilterRange = dayjs(
            effectiveEndDate,
          ).isBetween(filteredEffectiveStartDate, filteredEffectiveEndDate);

          const isFilterStartDateInEffectiveRange = dayjs(
            filteredEffectiveStartDate,
          ).isBetween(effectiveStartDate, effectiveEndDate);
          const isFilterEndDateInEffectiveRange = dayjs(
            filteredEffectiveEndDate,
          ).isBetween(effectiveStartDate, effectiveEndDate);

          return (
            isEffectiveStartDateInFilterRange ||
            isEffectiveEndDateInFilterRange ||
            isFilterStartDateInEffectiveRange ||
            isFilterEndDateInEffectiveRange
          );
        },
      );
    }

    return filteredRules;
  },
);

export default paymentOrchestrationRulesSlice.reducer;
